<template src="./paymentSchedule.html"></template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';
    import moment from 'moment';
    import { axiosRequests } from '../../../../services/axiosinstance';
    import { editUrl, getFormatedAmount, getFormatedDate, getFormatedPreviousDay, getFreezeStatus } from '../../../../services/helper';
    import { Routes } from '../../../../const';
    import { Contract } from '../../../../models/Contract';
    import { ContractPaymentSchedule, PaymentScheduleColumn, PaymentScheduleFreeze, PaymentScheduleItem } from '../../../../models/ContractPaymentSchedule';
    import PaymentExplanationModal from '../../../Order/PaymentExplanation/PaymentExplanationModal.vue';

    // Под самые длинные подписи вроде «−2 145,10 ₽» и «Приведи друга» с именем
    const MIN_COLUMN_WIDTH = 150;
    const MAX_PAST_COLUMNS_COUNT = 3;
    const MAX_BAR_HEIGHT = 110;
    // Мельче вычет не виден, а скругление верхнего угла рвёт его обводку
    const MIN_DEDUCTION_HEIGHT = 10;
    // У неоплаченного столбика без заливки иначе пропадает контур, например у платы за заморозку в 45 ₽
    const MIN_OUTLINED_HEIGHT = 8;
    // Подпись заморозки не должна вылезать за свою область: короткие заморозки стоят встык
    const FREEZE_FULL_LABEL_WIDTH = 112;
    const FREEZE_SHORT_LABEL_WIDTH = 40;
    const WIDE_SCREEN_QUERY = '(min-width: 768px)';

    const FITNESS_CONTRACT_SERVICE_TYPE = 'service_type.fitness_contract';
    const INVITE_FRIEND_DISCOUNT_TYPE = 'discount_type.invite_friend';
    const PROMO_CODE_DISCOUNT_TYPE = 'discount_type.promo_code';

    interface Segment {
        tone: string,
        height: number
    }

    interface Note {
        amount: string,
        tone: string,
        text: string,
        friendClientId: number | null,
        friendClientName: string | null,
        suffix: string
    }

    interface ColumnView {
        orderId: number | null,
        orderNumber: string,
        kind: string,
        center: number,
        amountText: string,
        isMuted: boolean,
        isPaid: boolean,
        isPaidLate: boolean,
        isFreeMonth: boolean,
        barHeight: number,
        segments: Segment[],
        dateText: string,
        statusText: string,
        periodText: string,
        notes: Note[]
    }

    interface FreezeView {
        freeze: PaymentScheduleFreeze,
        left: number,
        width: number,
        title: string,
        label: string,
        period: string
    }

    interface TooltipRow {
        name: string,
        note: string,
        amount: string,
        tone: string
    }

    interface Tooltip {
        style: Record<string, string>,
        title: string,
        subtitle: string,
        rows: TooltipRow[],
        totalLabel: string,
        total: string,
        notes: string[]
    }

    interface LegendEntry {
        tone: string,
        text: string
    }

    interface ComponentModel {
        schedule: ContractPaymentSchedule | null,
        width: number,
        isWideScreen: boolean,
        hovered: { type: string, index: number } | null,
        explanationOrderId: number | null,
        mediaQuery: MediaQueryList | null,
        resizeObserver: ResizeObserver | null
    }

    export default defineComponent({
        name: 'PaymentSchedule',
        props: {
            contract: { type: Object as PropType<Contract>, required: true }
        },
        components: { paymentExplanationModal: PaymentExplanationModal },
        data(): ComponentModel {
            return {
                schedule: null,
                width: 0,
                isWideScreen: false,
                hovered: null,
                explanationOrderId: null,
                mediaQuery: null,
                resizeObserver: null
            }
        },
        computed: {
            // Прошлые — до ближайшего выставленного заказа; дальше он и прогноз, они видны всегда
            upcomingStartIndex(): number {
                const columns = this.schedule?.columns ?? [];
                const index = columns.findIndex(c => c.kind == 'next' || c.orderId == null);

                return index == -1 ? columns.length : index;
            },
            visibleColumns(): PaymentScheduleColumn[] {
                const columns = this.schedule?.columns ?? [];
                const upcomingCount = columns.length - this.upcomingStartIndex;
                const pastCount = Math.min(MAX_PAST_COLUMNS_COUNT, Math.max(1, Math.floor(this.width / MIN_COLUMN_WIDTH) - upcomingCount));

                return columns.slice(Math.max(0, this.upcomingStartIndex - pastCount));
            },
            columnDays(): number[] {
                return this.visibleColumns.map(c => this.toDay(c.date));
            },
            gridStyle(): Record<string, string> {
                return { 'grid-template-columns': `repeat(${this.visibleColumns.length}, minmax(0, 1fr))` };
            },
            columnViews(): ColumnView[] {
                const columns = this.visibleColumns;
                const maxGross = Math.max(1, ...columns.map(c => c.gross));

                return columns.map((column, index) => {
                    const isFreeMonth = column.kind == 'free_month';
                    const segments = this.buildSegments(column, maxGross);
                    const dateText = getFormatedDate(column.date, 'DD.MM');
                    const dueDateText = getFormatedDate(column.dueDate, 'DD.MM');
                    // Заказ после отказа банка оплачен позже плановой даты, а клиент спрашивает про дату списания
                    const periodText = dueDateText && dueDateText != dateText
                        ? this.$t('contract.view.payment_schedule.for_period', { date: dueDateText })
                        : '';

                    return {
                        orderId: column.orderId,
                        orderNumber: column.orderId?.toString().padStart(8, '0') ?? '',
                        kind: column.kind,
                        center: (index + 0.5) / columns.length * 100,
                        amountText: `${getFormatedAmount(column.total)} ${this.schedule?.currencySign}`,
                        isMuted: column.orderId == null || isFreeMonth,
                        isPaid: column.kind == 'paid',
                        isPaidLate: column.kind == 'paid' && periodText != '',
                        isFreeMonth: isFreeMonth,
                        barHeight: segments.reduce((sum, s) => sum + s.height, 0),
                        segments: segments,
                        dateText: dateText,
                        statusText: this.getStatusText(column),
                        periodText: periodText,
                        notes: this.buildNotes(column)
                    };
                });
            },
            freezeViews(): FreezeView[] {
                return (this.schedule?.freezes ?? [])
                    .map(freeze => {
                        const left = this.toX(freeze.startDate);
                        const right = freeze.endDate ? this.toX(freeze.endDate) : 100;
                        const widthPx = (right - left) / 100 * this.width;
                        const isFullLabel = widthPx >= FREEZE_FULL_LABEL_WIDTH;

                        const title = freeze.daysCount
                            ? this.$t('contract.view.payment_schedule.freeze_days', { days: freeze.daysCount })
                            : this.$t('contract.view.payment_schedule.freeze');

                        const shortTitle = freeze.daysCount && widthPx >= FREEZE_SHORT_LABEL_WIDTH
                            ? this.$t('contract.view.payment_schedule.freeze_days_short', { days: freeze.daysCount })
                            : '';

                        return {
                            freeze: freeze,
                            left: left,
                            width: right - left,
                            title: title,
                            label: isFullLabel ? title : shortTitle,
                            period: !isFullLabel
                                ? ''
                                : freeze.endDate
                                    ? `${getFormatedDate(freeze.startDate, 'DD.MM')}–${getFormatedPreviousDay(freeze.endDate, 'DD.MM')}`
                                    : this.$t('contract.view.payment_schedule.since', { date: getFormatedDate(freeze.startDate, 'DD.MM') })
                        };
                    })
                    .filter(f => f.width > 0);
            },
            today(): { x: number, dateText: string } | null {
                if (!this.schedule) return null;

                const x = this.toX(this.schedule.today);

                // За краем окна отметка «сегодня» встала бы на крайний столбец и вводила бы в заблуждение
                if (x <= 0 || x >= 100) return null;

                return {
                    x: x,
                    dateText: getFormatedDate(this.schedule.today, 'DD.MM')
                };
            },
            timelinePastWidth(): number {
                if (this.today) return this.today.x;

                const days = this.columnDays;

                return this.schedule && days.length > 0 && this.toDay(this.schedule.today) > days[days.length - 1] ? 100 : 0;
            },
            legend(): LegendEntry[] {
                const entries = new Map<string, LegendEntry>();

                for (const column of this.visibleColumns) {
                    for (const item of column.items) {
                        if (item.amount > 0) {
                            entries.set(item.serviceTypeName, { tone: item.serviceTypeName == FITNESS_CONTRACT_SERVICE_TYPE ? 'contract' : 'service', text: this.$t(item.serviceTypeName) });
                        } else if (item.amount < 0) {
                            entries.set(item.isFreezeDiscount ? 'freeze' : item.discountTypeName ?? item.serviceTypeName, { tone: this.getDeductionTone(item), text: this.$t(item.isFreezeDiscount ? item.serviceTypeName : item.discountTypeName ?? item.serviceTypeName) });
                        }
                    }
                }

                // Статус передаёт заливка, а цвет — тип позиции, поэтому образцы статусов серые
                const result: LegendEntry[] = [];

                if (this.visibleColumns.some(c => c.kind == 'paid')) result.push({ tone: 'status_paid', text: this.$t('contract.view.payment_schedule.legend_paid') });
                if (this.visibleColumns.some(c => c.kind == 'next')) result.push({ tone: 'status_due', text: this.$t('contract.view.payment_schedule.legend_due') });
                if (this.visibleColumns.some(c => c.kind == 'forecast')) result.push({ tone: 'status_forecast', text: this.$t('contract.view.payment_schedule.legend_forecast') });

                result.push(...entries.values());

                if (this.freezeViews.length > 0) result.push({ tone: 'freeze_period', text: this.$t('contract.view.payment_schedule.legend_freeze_period') });
                if (this.visibleColumns.some(c => c.kind == 'free_month')) result.push({ tone: 'free_month', text: this.$t('contract.view.payment_schedule.legend_free_month') });

                return result;
            },
            tooltip(): Tooltip | null {
                if (!this.hovered) return null;

                return this.hovered.type == 'freeze'
                    ? this.buildFreezeTooltip(this.hovered.index)
                    : this.buildColumnTooltip(this.hovered.index);
            }
        },
        methods: {
            loadSchedule: async function () {
                await axiosRequests.get(editUrl(Routes.getContractPaymentSchedule, this.contract.id))
                    .then(
                        (data: ContractPaymentSchedule) => {
                            this.schedule = data;
                        }
                    )
                    .catch(
                        async (err: any) => {
                            // График — дополнительный раздел: при ошибке карточка контракта остаётся без него
                            this.schedule = null;

                            if (err.status == 401 || err.status == 403) {
                                return;
                            } else if (err.status == 400 && err.data) {
                                this.$store.commit('SET_ADD_ERROR', err.data);
                            } else {
                                this.$store.commit('SET_ADD_ERROR', 'error.something_went_wrong');
                            }
                        }
                    );
            },
            onScreenChange: function (event: MediaQueryListEvent) {
                this.isWideScreen = event.matches;
            },
            toDay: function (date: string): number {
                return moment.utc(moment.parseZone(date).format('YYYY-MM-DD')).valueOf();
            },
            // Столбцы стоят с равным шагом, поэтому дату между ними ставим кусочно-линейно по соседним платежам
            toX: function (date: string): number {
                const days = this.columnDays;
                const count = days.length;
                const day = this.toDay(date);

                if (count == 0) return 0;
                if (count == 1) return day < days[0] ? 0 : day > days[0] ? 100 : 50;

                let index = days.findIndex((d, i) => i < count - 1 && day >= d && day <= days[i + 1]);

                if (index == -1) index = day < days[0] ? 0 : count - 2;

                const span = days[index + 1] - days[index] || 1;
                const x = (index + 0.5 + (day - days[index]) / span) / count * 100;

                return Math.min(100, Math.max(0, x));
            },
            buildSegments: function (column: PaymentScheduleColumn, maxGross: number): Segment[] {
                const scale = MAX_BAR_HEIGHT / maxGross;

                if (column.kind == 'free_month') {
                    return [{ tone: 'free_month', height: Math.max(1, Math.round(column.gross * scale)) }];
                }

                // Сплошной — списано, контур — заказ ждёт списания, пунктир — заказа ещё нет
                const toneSuffix = column.orderId == null ? '_forecast' : column.kind == 'paid' ? '' : '_due';
                const minSolidHeight = toneSuffix ? MIN_OUTLINED_HEIGHT : 1;
                const deductions = column.items.filter(i => i.amount < 0);
                const positives = column.items
                    .filter(i => i.amount > 0)
                    .sort((a, b) => Number(b.serviceTypeName == FITNESS_CONTRACT_SERVICE_TYPE) - Number(a.serviceTypeName == FITNESS_CONTRACT_SERVICE_TYPE));

                // Вычет кладётся поверх абонемента: сплошная часть столбика — то, что списывается
                let uncovered = -deductions.reduce((sum, i) => sum + i.amount, 0);

                const solids = positives
                    .map(item => {
                        const covered = Math.min(item.amount, uncovered);
                        uncovered -= covered;

                        return { tone: (item.serviceTypeName == FITNESS_CONTRACT_SERVICE_TYPE ? 'contract' : 'service') + toneSuffix, amount: item.amount - covered };
                    })
                    .filter(s => s.amount > 0)
                    .reverse();

                return deductions
                    .map(item => ({ tone: this.getDeductionTone(item), height: Math.max(MIN_DEDUCTION_HEIGHT, Math.round(-item.amount * scale)) }))
                    .concat(solids.map(s => ({ tone: s.tone, height: Math.max(minSolidHeight, Math.round(s.amount * scale)) })));
            },
            getDeductionTone: function (item: PaymentScheduleItem): string {
                if (item.isFreezeDiscount) return 'freeze';
                if (item.discountTypeName == INVITE_FRIEND_DISCOUNT_TYPE) return 'invite_friend';

                return 'promo_code';
            },
            getStatusText: function (column: PaymentScheduleColumn): string {
                if (column.kind == 'free_month') {
                    return this.$t(column.orderId == null ? 'contract.view.payment_schedule.status_forecast' : 'contract.view.payment_schedule.status_free_month');
                }

                return this.$t('contract.view.payment_schedule.status_' + column.kind);
            },
            getItemDescription: function (item: PaymentScheduleItem): string {
                if (item.amount > 0) return item.name;
                if (item.isFreezeDiscount) return item.freezeDays ? this.$t('contract.view.payment_schedule.freeze_days', { days: item.freezeDays }) : this.$t(item.serviceTypeName);
                if (item.discountTypeName == PROMO_CODE_DISCOUNT_TYPE && item.promoCode) return this.$t('contract.view.payment_schedule.promo_code', { code: item.promoCode });

                return this.$t(item.discountTypeName ?? item.serviceTypeName);
            },
            getExpectedSuffix: function (item: PaymentScheduleItem): string {
                return item.expectedFrom
                    ? `, ${this.$t('contract.view.payment_schedule.since', { date: getFormatedDate(item.expectedFrom, 'DD.MM') })}`
                    : '';
            },
            buildNotes: function (column: PaymentScheduleColumn): Note[] {
                return column.items
                    .filter(i => i.amount < 0 || (i.amount > 0 && i.serviceTypeName != FITNESS_CONTRACT_SERVICE_TYPE))
                    .map(item => ({
                        amount: this.formatSignedAmount(item.amount),
                        tone: column.kind == 'free_month' ? 'muted' : item.amount > 0 ? 'service' : this.getDeductionTone(item),
                        text: this.getItemDescription(item),
                        friendClientId: item.friendClientName ? item.friendClientId : null,
                        friendClientName: item.friendClientName,
                        suffix: this.getExpectedSuffix(item)
                    }));
            },
            buildColumnTooltip: function (index: number): Tooltip | null {
                const column = this.visibleColumns[index];
                const view = this.columnViews[index];

                if (!column || !view) return null;

                const notes: string[] = [];

                if (column.kind == 'free_month') {
                    notes.push(column.movedTo
                        ? this.$t('contract.view.payment_schedule.free_month_moved_note', { date: getFormatedDate(column.movedTo, 'DD.MM.YYYY') })
                        : this.$t('contract.view.payment_schedule.free_month_note'));
                }

                for (const item of column.items.filter(i => i.expectedFrom)) {
                    notes.push(this.$t('contract.view.payment_schedule.expected_referral_note', { date: getFormatedDate(item.expectedFrom, 'DD.MM') }));
                }

                if (column.orderId == null) {
                    notes.push(this.$t('contract.view.payment_schedule.forecast_note'));

                    const columnDay = this.toDay(column.date);
                    const today = this.toDay(this.schedule?.today ?? column.date);

                    if (this.schedule?.freezes.some(f => this.toDay(f.startDate) > today && this.toDay(f.startDate) <= columnDay)) {
                        notes.push(this.$t('contract.view.payment_schedule.forecast_freeze_note'));
                    }
                } else {
                    notes.push(this.$t('contract.view.payment_schedule.click_hint'));
                }

                const totalLabelKey = column.orderId == null ? 'total_forecast' : column.kind == 'paid' ? 'total_paid' : 'total_next';

                return {
                    style: this.getTooltipPosition(view.center),
                    title: `${getFormatedDate(column.date, 'DD.MM.YYYY')} · ${view.statusText}`,
                    subtitle: '',
                    rows: column.items.map(item => ({
                        name: item.name,
                        note: item.amount < 0
                            ? `${this.getItemDescription(item)}${item.friendClientName ? ': ' + item.friendClientName : ''}${this.getExpectedSuffix(item)}`
                            : '',
                        amount: this.formatMoney(item.amount),
                        tone: item.amount < 0 ? this.getDeductionTone(item) : 'default'
                    })),
                    totalLabel: this.$t('contract.view.payment_schedule.' + totalLabelKey),
                    total: this.formatMoney(column.total),
                    notes: notes
                };
            },
            buildFreezeTooltip: function (index: number): Tooltip | null {
                const view = this.freezeViews[index];

                if (!view) return null;

                const freeze = view.freeze;

                const period = freeze.endDate
                    ? `${getFormatedDate(freeze.startDate, 'DD.MM.YYYY')}–${getFormatedPreviousDay(freeze.endDate, 'DD.MM.YYYY')}`
                    : this.$t('contract.view.payment_schedule.since', { date: getFormatedDate(freeze.startDate, 'DD.MM.YYYY') });

                return {
                    style: this.getTooltipPosition(view.left),
                    title: view.title,
                    subtitle: this.$t('freeze_status.' + getFreezeStatus(freeze.status)),
                    rows: [{ name: period, note: '', amount: '', tone: 'default' }]
                        .concat(freeze.feeOrders.map(fee => ({
                            name: this.$t('contract.view.payment_schedule.freeze_fee'),
                            note: `#${fee.orderId.toString().padStart(8, '0')}`,
                            amount: this.formatMoney(fee.amount),
                            tone: 'default'
                        }))),
                    totalLabel: '',
                    total: '',
                    notes: []
                };
            },
            getTooltipPosition: function (anchor: number): Record<string, string> {
                return anchor >= 50
                    ? { right: `calc(${(100 - anchor).toFixed(2)}% + 34px)` }
                    : { left: `calc(${anchor.toFixed(2)}% + 34px)` };
            },
            formatSignedAmount: function (amount: number): string {
                return `${amount < 0 ? '−' : '+'}${getFormatedAmount(Math.abs(amount))} ${this.schedule?.currencySign}`;
            },
            formatMoney: function (amount: number): string {
                const text = (Math.abs(amount) / 100).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

                return `${amount < 0 ? '−' : ''}${text} ${this.schedule?.currencySign}`;
            },
            openExplanation: function (orderId: number | null) {
                if (!orderId) return;

                this.hovered = null;
                this.explanationOrderId = orderId;
            },
            openFriend: function (friendClientId: number) {
                this.$store.commit('SET_LAST_VIEWED_CLIENT', { fi: "", id: friendClientId, buttonText: this.contract.clientName, buttonPath: `/client/${this.contract.clientId}`, isNeedTranslatedButtonText: false });

                this.$router.push(`/client/${friendClientId}`);
            }
        },
        watch: {
            contract: function () {
                if (this.isWideScreen) this.loadSchedule();
            },
            isWideScreen: function (isWide: boolean) {
                if (isWide && !this.schedule) this.loadSchedule();
            }
        },
        mounted() {
            const root = this.$refs.root as HTMLElement;

            this.width = root.clientWidth;
            this.resizeObserver = new ResizeObserver(() => { this.width = root.clientWidth; });
            this.resizeObserver.observe(root);

            this.mediaQuery = window.matchMedia(WIDE_SCREEN_QUERY);
            this.mediaQuery.addEventListener('change', this.onScreenChange);
            this.isWideScreen = this.mediaQuery.matches;
        },
        beforeUnmount() {
            this.resizeObserver?.disconnect();
            this.mediaQuery?.removeEventListener('change', this.onScreenChange);
        }
    });
</script>

<style scoped>
    .payment_schedule_section {
        display: flex;
        flex-flow: column;
        gap: 12px;
        margin-top: 24px;
    }

    .payment_schedule_legend {
        display: flex;
        flex-wrap: wrap;
        gap: 6px 18px;
        font-size: 12px;
        color: #65656F;
    }

    .payment_schedule_legend_entry {
        display: flex;
        align-items: center;
        gap: 6px;
    }

    .payment_schedule_swatch {
        width: 10px;
        height: 10px;
        box-sizing: border-box;
        border-radius: 2px;
    }

    .payment_schedule_chart {
        position: relative;
        padding-top: 24px;
    }

    /* Подписи заморозок — в полосе над столбиками, иначе они наезжают на суммы */
    .payment_schedule_chart_with_freezes {
        padding-top: 52px;
    }

    .payment_schedule_chart_with_freezes .payment_schedule_today_line {
        height: 182px;
    }

    .payment_schedule_chart_with_freezes .payment_schedule_freeze {
        height: 178px;
    }

    .payment_schedule_today_line {
        position: absolute;
        top: 20px;
        height: 154px;
        border-left: 1px dashed #df2793;
    }

    .payment_schedule_today_label {
        position: absolute;
        top: 2px;
        transform: translateX(-50%);
        font-size: 11px;
        font-weight: 600;
        color: #c21f7e;
        white-space: nowrap;
    }

    .payment_schedule_freeze {
        position: absolute;
        top: 24px;
        height: 150px;
        box-sizing: border-box;
        border-radius: 8px 8px 0 0;
        border-top: 2px solid #9cc6ea;
        background: repeating-linear-gradient(135deg, rgba(74, 144, 207, 0.13) 0 5px, rgba(74, 144, 207, 0) 5px 10px);
    }

    .payment_schedule_freeze_label {
        position: absolute;
        top: 5px;
        left: 8px;
        right: 4px;
        overflow: hidden;
        display: flex;
        flex-flow: column;
        gap: 1px;
        font-size: 11px;
        line-height: 1.3;
        color: #1f5f99;
        white-space: nowrap;
        pointer-events: none;
    }

    .payment_schedule_bars {
        position: relative;
        display: grid;
        height: 150px;
        pointer-events: none;
    }

    .payment_schedule_bar_button {
        display: flex;
        flex-flow: column;
        justify-content: flex-end;
        align-items: center;
        gap: 6px;
        padding: 0px;
        border: none;
        background: transparent;
        font-family: inherit;
        cursor: default;
    }

    .payment_schedule_bar_button > * {
        pointer-events: auto;
    }

    .payment_schedule_bar_button_clickable {
        cursor: pointer;
    }

    .payment_schedule_amount {
        font-size: 15px;
        font-weight: 700;
        white-space: nowrap;
        color: #2b2b33;
    }

    .payment_schedule_amount_muted {
        color: #8a8a93;
    }

    .payment_schedule_bar {
        display: flex;
        flex-flow: column;
        width: 44px;
        border-radius: 6px 6px 0 0;
        overflow: hidden;
    }

    .payment_schedule_segment {
        display: block;
        box-sizing: border-box;
        flex-shrink: 0;
    }

    .payment_schedule_segment:first-child {
        border-radius: 6px 6px 0 0;
    }

    .payment_schedule_segment_contract,
    .payment_schedule_segment_service {
        border-top: 1px solid #ffffff;
    }

    .payment_schedule_segment_contract {
        background: #0aa2b8;
    }

    .payment_schedule_segment_service {
        background: #8a7fd6;
    }

    .payment_schedule_segment_contract_due {
        background: #d5f0f4;
        box-shadow: inset 0 0 0 2px #0aa2b8;
    }

    .payment_schedule_segment_service_due {
        background: #e6e3f7;
        box-shadow: inset 0 0 0 2px #8a7fd6;
    }

    .payment_schedule_segment_contract_forecast {
        background: #ffffff;
        border: 2px dashed #0aa2b8;
    }

    .payment_schedule_segment_service_forecast {
        background: #ffffff;
        border: 2px dashed #8a7fd6;
    }

    .payment_schedule_segment_status_paid {
        background: #9aa0a6;
    }

    .payment_schedule_segment_status_due {
        background: #eef0f2;
        box-shadow: inset 0 0 0 2px #9aa0a6;
    }

    .payment_schedule_segment_status_forecast {
        background: #ffffff;
        border: 2px dashed #9aa0a6;
    }

    .payment_schedule_segment_freeze {
        background: repeating-linear-gradient(135deg, #94c1ea 0 3px, #ffffff 3px 6px);
        box-shadow: inset 0 0 0 1px #94c1ea;
    }

    .payment_schedule_segment_invite_friend {
        background: repeating-linear-gradient(135deg, #ee9cc8 0 3px, #ffffff 3px 6px);
        box-shadow: inset 0 0 0 1px #ee9cc8;
    }

    .payment_schedule_segment_promo_code {
        background: repeating-linear-gradient(135deg, #f3bb72 0 3px, #ffffff 3px 6px);
        box-shadow: inset 0 0 0 1px #f3bb72;
    }

    .payment_schedule_segment_free_month {
        background: #eef0f2;
        box-shadow: inset 0 0 0 1px #d3d6db;
    }

    .payment_schedule_segment_freeze_period {
        width: 16px;
        height: 12px;
        border-radius: 3px 3px 0 0;
        border-top: 2px solid #9cc6ea;
        background: repeating-linear-gradient(135deg, rgba(74, 144, 207, 0.18) 0 3px, rgba(74, 144, 207, 0) 3px 6px);
    }

    .payment_schedule_tooltip {
        position: absolute;
        top: 24px;
        z-index: 5;
        width: 300px;
        box-sizing: border-box;
        display: flex;
        flex-flow: column;
        gap: 10px;
        padding: 14px;
        border-radius: 14px;
        background: #ffffff;
        border: 1px solid #e3e5e8;
        box-shadow: 0 8px 24px rgba(20, 30, 40, 0.12);
        pointer-events: none;
    }

    .payment_schedule_tooltip_header {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 12px;
    }

    .payment_schedule_tooltip_title {
        font-size: 14px;
        font-weight: 700;
        white-space: nowrap;
    }

    .payment_schedule_tooltip_subtitle {
        font-size: 12px;
        color: #65656F;
        white-space: nowrap;
    }

    .payment_schedule_tooltip_rows {
        display: flex;
        flex-flow: column;
        gap: 8px;
    }

    .payment_schedule_tooltip_row {
        display: flex;
        gap: 12px;
        align-items: flex-start;
    }

    .payment_schedule_tooltip_row_text {
        flex: 1 1 auto;
        display: flex;
        flex-flow: column;
        gap: 1px;
        min-width: 0;
        font-size: 13px;
        color: #2b2b33;
    }

    .payment_schedule_tooltip_row_note {
        font-size: 12px;
        line-height: 1.35;
        color: #65656F;
    }

    .payment_schedule_tooltip_row_amount {
        font-size: 13px;
        font-weight: 700;
        white-space: nowrap;
    }

    .payment_schedule_tooltip_total {
        display: flex;
        justify-content: space-between;
        padding-top: 8px;
        border-top: 1px solid #eaebec;
        font-size: 14px;
        font-weight: 700;
    }

    .payment_schedule_tooltip_note {
        font-size: 12px;
        line-height: 1.4;
        color: #65656F;
    }

    .payment_schedule_timeline {
        position: relative;
        height: 16px;
    }

    .payment_schedule_timeline::before {
        content: "";
        position: absolute;
        top: 0px;
        left: 0px;
        right: 0px;
        height: 4px;
        border-radius: 2px;
        background: #cfd3d8;
    }

    .payment_schedule_timeline_past {
        position: absolute;
        top: 0px;
        left: 0px;
        height: 4px;
        border-radius: 2px 0 0 2px;
        background: #0aa2b8;
    }

    .payment_schedule_pin {
        position: absolute;
        top: -3px;
        transform: translateX(-50%);
        width: 10px;
        height: 10px;
        box-sizing: border-box;
        border-radius: 50%;
        outline: 2px solid #ffffff;
        background: #ffffff;
        box-shadow: inset 0 0 0 2px #9aa0a6;
    }

    .payment_schedule_pin_paid {
        box-shadow: inset 0 0 0 2px #0aa2b8;
    }

    .payment_schedule_pin_paid_late {
        box-shadow: inset 0 0 0 2px #e09a1f;
    }

    .payment_schedule_period {
        font-size: 12px;
        color: #b26a00;
    }

    .payment_schedule_labels {
        display: grid;
    }

    .payment_schedule_label {
        display: flex;
        flex-flow: column;
        align-items: center;
        gap: 2px;
        padding: 0px 8px;
        text-align: center;
    }

    .payment_schedule_date {
        font-size: 14px;
        font-weight: 600;
        color: #2b2b33;
    }

    .payment_schedule_date_cancelled {
        color: #8a8a93;
        text-decoration: line-through;
    }

    .payment_schedule_status {
        font-size: 12px;
        color: #6b6b75;
    }

    .payment_schedule_status_paid {
        color: #1d6b3a;
    }

    .payment_schedule_status_next {
        color: #8a5a00;
    }

    .payment_schedule_status_free_month {
        color: #8a8a93;
    }

    .payment_schedule_link {
        font-size: 12px;
        color: #0a8fa3;
        text-decoration: underline;
        text-underline-offset: 2px;
    }

    .payment_schedule_note {
        display: flex;
        flex-flow: column;
        align-items: center;
        margin-top: 6px;
    }

    .payment_schedule_note_amount {
        font-size: 13px;
        font-weight: 700;
        white-space: nowrap;
    }

    .payment_schedule_note_text {
        font-size: 12px;
        line-height: 1.35;
        color: #65656F;
    }

    .payment_schedule_friend_link {
        margin-left: 4px;
    }

    .payment_schedule_note_default {
        color: #2b2b33;
    }

    .payment_schedule_note_service {
        color: #5a4fb0;
    }

    .payment_schedule_note_freeze {
        color: #1f5f99;
    }

    .payment_schedule_note_invite_friend {
        color: #b0186d;
    }

    .payment_schedule_note_promo_code {
        color: #9a5400;
    }

    .payment_schedule_note_muted {
        color: #8a8a93;
    }
</style>
